import {HalImageChannelLayout} from "@cm/hal"
import {Region, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {Color, isColorLike, Size2Like} from "@cm/math"
import {assertNever} from "@cm/utils"
import {z} from "zod/v4-mini"
import {AddressMode, remotelyColorLike} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {applyWrapToRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"

export {isImageOpHal} from "../../../../../operator-stack/image-op-system/detail/abstract/hal/image-op-hal"

export const applyAddressModeToRegion = (fullImageSize: Size2Like, region: Region, mipLevel: number, addressMode: AddressMode): Regions => {
    if (addressMode === "wrap") {
        return applyWrapToRegion(fullImageSize, region, mipLevel)
    } else if (addressMode === "wrap-mirrored") {
        throw new Error("Not implemented") // TODO
    } else {
        return [region]
    }
}

export const channelLayoutByNumChannels = (channels: number): HalImageChannelLayout => {
    switch (channels) {
        case 1:
            return "R"
        case 3:
            return "RGB"
        case 4:
            return "RGBA"
        default:
            throw new Error("Unsupported number of channels")
    }
}

export const glslVectorTypeByNumChannels = (channels: number): string => {
    switch (channels) {
        case 1:
            return "float"
        case 2:
            return "vec2"
        case 3:
            return "vec3"
        case 4:
            return "vec4"
        default:
            throw new Error("Unsupported number of channels")
    }
}

export const indexGlslVectorType = (channels: number, channelIndex: number) => {
    if (channelIndex < 0 || channelIndex >= channels) {
        throw new Error("Invalid channel index")
    }
    switch (channels) {
        case 1:
            return ""
        case 2:
            switch (channelIndex) {
                case 0:
                    return ".r"
                case 1:
                    return ".g"
            }
            throw new Error("Invalid channel index")
        case 3:
            switch (channelIndex) {
                case 0:
                    return ".r"
                case 1:
                    return ".g"
                case 2:
                    return ".b"
            }
            throw new Error("Invalid channel index")
        case 4:
            switch (channelIndex) {
                case 0:
                    return ".r"
                case 1:
                    return ".g"
                case 2:
                    return ".b"
                case 3:
                    return ".a"
            }
            throw new Error("Invalid channel index")
        default:
            throw new Error("Unsupported number of channels")
    }
}

export const colorFromRemotelyColorLike = (sourceValue: z.infer<typeof remotelyColorLike>): Color => {
    if (Array.isArray(sourceValue)) {
        if (sourceValue.length !== 4) {
            throw new Error("colorFromRemotelyColorLike: Invalid source value length")
        }
        return new Color(sourceValue[0], sourceValue[1], sourceValue[2], sourceValue[3])
    } else if (isColorLike(sourceValue)) {
        return new Color(sourceValue.r, sourceValue.g, sourceValue.b, sourceValue.a ?? 1)
    } else if (typeof sourceValue === "number") {
        return new Color(sourceValue, sourceValue, sourceValue, sourceValue)
    } else {
        assertNever(sourceValue)
        throw new Error("colorFromRemotelyColorLike: Invalid source value type")
    }
}
